<!--
  Functions as an option picker 
  where only one option can be selected at any one time.
-->
<template>
  <div class="button-picker" :class="cssClass">
    <button
      v-for="(option, $index) in options"
      :key="getKey(option)"
      type="button"
      class="picker-option"
      :class="{ active: isSelected($index) }"
      :disabled="disabled"
      @click="handleClick($index)"
    >
      <svg-icon v-if="option.iconName" :name="option.iconName" kind="action" />
      <span v-else>{{ buttonLabel(option) }}</span>

      <cv-tooltip
        v-if="showTooltip"
        :tip="option.tooltipMessage"
        alignment="center"
        direction="bottom"
      ></cv-tooltip>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";

// Props
interface ButtonPickerOption {
  value: string;
  label?: string;
  iconName?: string;
  tooltipMessage?: string;
}

interface Props {
  selectedOption?: number; // index of selected option
  options: ButtonPickerOption[];
  disabled?: boolean;
  type?: string; // Primary - pale blue background, Secondary - white background
  showTooltip?: boolean;
}

const {
  selectedOption = 0,
  options = [],
  disabled = false,
  type = "primary",
  showTooltip = false,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  selected: [index: number];
}>();

// Reactive data
const optionSelected = ref(0);

// Computed properties
const cssClass = computed(() => [`${type}`, { "has-tooltip": showTooltip }]);

const isSelected = computed(() => {
  return (optionIndex: number) => {
    return optionSelected.value === optionIndex;
  };
});

// Methods
const getKey = (option: { label?: string; iconName?: string }) => {
  return `${Date.now()}_${option.label || option.iconName || option}`;
};

const buttonLabel = (option: string | ButtonPickerOption) => {
  return typeof option === "string" ? option : (option.label ?? "");
};

const handleClick = (val: number) => {
  optionSelected.value = val;
  emit("selected", val);
};

// Watchers
watch(
  () => selectedOption,
  (val) => {
    // Check if selected option is in range.
    if (Object.keys(options).includes(`${val}`)) {
      optionSelected.value = val;
      emit("selected", val);
    }
  },
);

// Lifecycle
onMounted(() => {
  if (selectedOption !== 0) {
    optionSelected.value = selectedOption;
  }
});
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.button-picker {
  display: flex;
  align-items: center;
  justify-content: center;

  &.secondary {
    .picker-option {
      background-color: $ui-02;

      &:not([disabled]) {
        &:hover {
          background-color: $hover-ui;
          color: $text-01;
        }

        &.active {
          background-color: $ui-05;
          color: $text-04;
        }
      }
    }
  }

  &.has-tooltip {
    .picker-option {
      position: relative;
      display: inline-flex;
      padding: $spacing-03;

      &.active {
        .sls-icon {
          [fill] {
            fill: $ui-01;
          }
        }
      }
    }

    .bx--tooltip__trigger {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;

      &:before {
        bottom: 0 !important;
      }
      .bx--assistive-text {
        bottom: -4px !important;
      }

      svg {
        display: none;
      }
    }
  }

  .picker-option {
    @include carbon--type-style("body-short-01", true);
    position: relative;
    padding: $spacing-03 $spacing-05;
    background-color: $ui-01;
    color: $text-02;
    border: 1px solid transparent;
    cursor: pointer;

    &[disabled] {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &:first-child {
      border-radius: 10px 0 0 10px;
    }

    &:last-child {
      border-radius: 0 10px 10px 0;
    }

    &:hover {
      &:not([disabled], .active) {
        background-color: $hover-ui;
      }
    }

    &:not(:first-child):not(.active) {
      &::before {
        @include position-mixin(
          $height: 1.5rem,
          $width: 1px,
          $left: -1px,
          $top: 50%,
          $pseudo: true
        );
        display: block;
        background-color: $ui-03;
        transform: translateY(-50%);
        z-index: 2;
      }
    }

    &.active {
      background-color: $ui-05;
      color: $inverse-01;
    }
  }
}
</style>
